import AddCommentIcon from '@mui/icons-material/AddComment'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditIcon from '@mui/icons-material/Edit'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'
import MenuIcon from '@mui/icons-material/Menu'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import PersonAddIcon from '@mui/icons-material/PersonAddAlt'
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useQuery, useQueryClient } from 'react-query'
import AgentPicker from '../../features/agent-picker/AgentPicker'
import ConversationSidebar from '../../features/conversation-list/ConversationSidebar'
import { useConversations } from '../../features/conversation-list/useConversations'
import DeleteConversationDialog from '../../features/delete-dialog/DeleteConversationDialog'
import MessageInput from '../../features/message-input/MessageInput'
import MessageThread from '../../features/message-thread/MessageThread'
import RenameConversationDialog from '../../features/rename-dialog/RenameConversationDialog'
import ShareConversationDialog from '../../features/share-dialog/ShareConversationDialog'
import {
  carryOverThoughts,
  countVisibleTurns,
  friendlyError,
  isTransportDrop,
  pendingQuestionIndex,
  recoverDroppedStream,
  withThoughtsForQuestion,
} from '../../helpers'
import { createCaApiClient } from '../../shared/CaApiClient'
import type {
  AgentOption,
  CaApiClient,
  CaBackend,
  CaReply,
  CaStreaming,
  Turn,
} from '../../shared/types'

export interface CaChatPanelProps {
  open: boolean
  onClose: () => void
  client?: CaApiClient
  apiBaseUrl?: string
  authToken?: string | (() => string | undefined)
  width?: number
  initialContext?: { dashboardId: string; title?: string; agentName?: string }
}

export default function CaChatPanel(props: CaChatPanelProps) {
  const intl = useIntl()
  const {
    open,
    onClose,
    apiBaseUrl,
    authToken,
    width = 480,
    initialContext,
  } = props
  const [fullscreen, setFullscreen] = useState(false)
  const [panelWidth, setPanelWidth] = useState(width)
  const [dragging, setDragging] = useState(false)
  const draggingRef = useRef(false)
  const bootCreateRef = useRef(false)
  const startDrag = (e: React.MouseEvent) => {
    e.preventDefault()
    draggingRef.current = true
    setDragging(true)
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'col-resize'
  }
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!draggingRef.current) {
        return
      }
      const w = window.innerWidth - e.clientX
      setPanelWidth(Math.min(Math.max(w, 360), window.innerWidth))
    }
    const stop = () => {
      if (!draggingRef.current) {
        return
      }
      draggingRef.current = false
      setDragging(false)
      document.body.style.userSelect = ''
      document.body.style.cursor = ''
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', stop)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', stop)
    }
  }, [])
  const client = useMemo(
    () => props.client ?? createCaApiClient({ apiBaseUrl, authToken }),
    [props.client, apiBaseUrl, authToken],
  )
  const qc = useQueryClient()
  const [activeId, setActiveId] = useState<string | undefined>()
  const [shareId, setShareId] = useState<string | undefined>()
  const [deleteId, setDeleteId] = useState<string | undefined>()
  const [renameId, setRenameId] = useState<string | undefined>()
  const [navOpen, setNavOpen] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [toast, setToast] = useState<string | undefined>()
  const [pickingAgent, setPickingAgent] = useState(false)
  const [createError, setCreateError] = useState<string | undefined>()

  const {
    list: conversations,
    create,
    rename,
    dropLocal,
  } = useConversations({
    client,
    dashboardId: initialContext?.dashboardId,
    enabled: open,
    onCreated: (c) => {
      setCreateError(undefined)
      setPickingAgent(false)
      setActiveId(c.id)
    },
    onCreateError: (e) => {
      setCreateError(
        friendlyError(e, intl.formatMessage({ id: 'caChat.panel.error.fallback' })),
      )
      if (!initialContext) {
        setPickingAgent(true)
      }
    },
    onRenamed: () => setRenameId(undefined),
  })
  const messages = useQuery(
    ['ca', 'messages', activeId],
    async () => {
      const server = await client.getMessages(activeId as string)
      const cached = qc.getQueryData<Turn[]>(['ca', 'messages', activeId]) ?? []
      return countVisibleTurns(server) >= countVisibleTurns(cached)
        ? carryOverThoughts(server, cached)
        : cached
    },
    {
      enabled: open && !!activeId,
      refetchOnWindowFocus: false,
    },
  )
  const agents = useQuery(['ca', 'agents'], () => client.listAgents(), {
    enabled: open && !initialContext,
  })
  const agentOptions = agents.data?.agents ?? []
  const supportedBackends = agents.data?.backends ?? []

  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState<string | undefined>()
  const [streaming, setStreaming] = useState<CaStreaming | null>(null)

  const handleSend = (q: string): boolean | void => {
    if (!activeId) {
      setSendError(intl.formatMessage({ id: 'caChat.panel.error.noConversation' }))
      return false
    }
    void doSend(q)
  }

  const doSend = async (q: string) => {
    if (!activeId) {
      return
    }
    setSendError(undefined)
    setSending(true)
    const baselineTurns = (
      qc.getQueryData<Turn[]>(['ca', 'messages', activeId]) ?? []
    ).length
    const thoughts: string[] = []
    setStreaming({
      question: q,
      text: '',
      thinking: true,
      thoughts: [],
      turnsBefore: baselineTurns,
      conversationId: activeId,
    })
    try {
      let reply: CaReply | undefined
      if (client.askStream) {
        try {
          reply = await client.askStream(activeId, q, {
            onThought: (t) => {
              thoughts.push(t)
              setStreaming((s) =>
                s
                  ? {
                      ...s,
                      thinking: true,
                      thought: t,
                      thoughts: [...(s.thoughts ?? []), t],
                    }
                  : s,
              )
            },
            onText: (full) =>
              setStreaming((s) =>
                s ? { ...s, text: full, thinking: false } : s,
              ),
          })
        } catch (e) {
          if (e instanceof Error && e.message === 'CA_STREAM_UNSUPPORTED') {
            reply = await client.ask(activeId, q)
          } else if (isTransportDrop(e)) {
            setStreaming((s) =>
              s
                ? {
                    ...s,
                    thinking: true,
                    thought: intl.formatMessage({ id: 'caChat.panel.stream.recovering' }),
                  }
                : s,
            )
            const recovered = await recoverDroppedStream(
              client,
              activeId,
              q,
              baselineTurns,
            )
            if (!recovered) {
              throw e
            }
            await qc.invalidateQueries(['ca', 'messages', activeId])
            qc.setQueryData<Turn[]>(['ca', 'messages', activeId], (prev) =>
              withThoughtsForQuestion(prev ?? [], q, baselineTurns, thoughts),
            )
          } else {
            throw e
          }
        }
      } else {
        reply = await client.ask(activeId, q)
      }
      if (reply) {
        await qc.cancelQueries(['ca', 'messages', activeId])
        const echoed = reply
        const now = new Date().toISOString()
        qc.setQueryData<Turn[]>(['ca', 'messages', activeId], (prev) => [
          ...(prev ?? []).slice(0, pendingQuestionIndex(prev ?? [], q, baselineTurns)),
          { role: 'user', text: q, createdAt: now },
          {
            role: 'assistant',
            text: echoed.text,
            tables: echoed.tables,
            charts: echoed.charts,
            thoughts: thoughts.length ? thoughts : undefined,
            createdAt: now,
          },
        ])
        setStreaming(null)
        // eslint-disable-next-line no-console
        console.log(
          '[ca:echo] conv=%s cache now %d turns (appended user+assistant)',
          activeId.slice(0, 8),
          (qc.getQueryData<Turn[]>(['ca', 'messages', activeId]) ?? []).length,
        )
        void qc.invalidateQueries(['ca', 'messages', activeId])
      }
      qc.invalidateQueries(['ca', 'conversations'])
    } catch (e) {
      setSendError(friendlyError(e, intl.formatMessage({ id: 'caChat.panel.error.fallback' })))
      void qc.invalidateQueries(['ca', 'messages', activeId])
    } finally {
      setSending(false)
      setStreaming(null)
    }
  }

  const pickAgent = (backend: CaBackend, agent?: AgentOption) => {
    setCreateError(undefined)
    create.mutate({ backend, agent })
  }

  const startNew = () => {
    setNavOpen(false)
    setActiveId(undefined)
    if (initialContext) {
      create.mutate({ dashboardId: initialContext.dashboardId })
      return
    }
    setPickingAgent(true)
  }

  useEffect(() => {
    if (!open || activeId || pickingAgent || create.isLoading) {
      return
    }
    if (initialContext) {
      if (!conversations.isSuccess) {
        return
      }
      const existingBound = conversations.data?.[0]?.id
      if (existingBound) {
        setActiveId(existingBound)
        return
      }
      if (!bootCreateRef.current) {
        bootCreateRef.current = true
        create.mutate({ dashboardId: initialContext.dashboardId })
      }
      return
    }
    const existing = conversations.data?.[0]?.id
    if (existing) {
      setActiveId(existing)
      return
    }
    if (conversations.isSuccess) {
      setPickingAgent(true)
    }
  }, [
    open,
    activeId,
    pickingAgent,
    conversations.data,
    conversations.isSuccess,
    initialContext,
    create.isLoading,
  ])

  const activeConversation = conversations.data?.find((c) => c.id === activeId)
  const activeAccess = activeConversation?.access
  const readOnly = activeAccess === 'viewer'
  const activeTitle =
    activeConversation?.title ||
    initialContext?.title ||
    intl.formatMessage({ id: 'caChat.panel.title.fallback' })
  const isOwner =
    !!activeConversation && (activeAccess === 'owner' || activeAccess == null)
  const renameTitle = conversations.data?.find((c) => c.id === renameId)?.title
  const activeAgent = agentOptions.find(
    (a) => !!activeConversation?.agentId && a.id === activeConversation.agentId,
  )
  const activeAgentLabel =
    activeAgent?.name ??
    initialContext?.agentName ??
    (activeConversation?.agentId
      ? activeConversation.backend === 'looker'
        ? intl.formatMessage({ id: 'caChat.panel.agentLabel.looker' })
        : intl.formatMessage({ id: 'caChat.panel.agentLabel.gcp' })
      : undefined)

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: fullscreen ? '100vw' : panelWidth,
          maxWidth: '100vw',
          transition: dragging ? 'none' : 'width 0.2s ease',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {!fullscreen && (
          <Box
            aria-hidden={true}
            onMouseDown={startDrag}
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '6px',
              cursor: 'col-resize',
              zIndex: 1,
              '&:hover': { bgcolor: 'primary.main', opacity: 0.4 },
            }}
          />
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0,
            pl: 0.5,
            pr: 0.5,
            py: 0.25,
          }}
        >
          <Tooltip title={intl.formatMessage({ id: 'caChat.panel.tooltip.conversations' })}>
            <IconButton
              aria-label={intl.formatMessage({ id: 'caChat.panel.aria.conversations' })}
              onClick={() => setNavOpen(true)}
              size="small"
              sx={{ p: 0.5 }}
            >
              <MenuIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography
            variant="h6"
            noWrap={true}
            sx={{ flex: 1, minWidth: 0, fontSize: '1.05rem' }}
          >
            {activeTitle}
          </Typography>
          {activeAgentLabel && (
            <Tooltip title={intl.formatMessage({ id: 'caChat.panel.tooltip.agentFixed' })}>
              <Chip
                size="small"
                variant="outlined"
                label={activeAgentLabel}
                sx={{ mr: 0.25, maxWidth: 140 }}
              />
            </Tooltip>
          )}
          <Tooltip title={intl.formatMessage({ id: 'caChat.panel.tooltip.newConversation' })}>
            <IconButton
              aria-label={intl.formatMessage({ id: 'caChat.panel.aria.newConversation' })}
              onClick={startNew}
              disabled={create.isLoading}
              size="small"
              sx={{ p: 0.5 }}
            >
              <AddCommentIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={intl.formatMessage({ id: 'caChat.panel.tooltip.conversationOptions' })}>
            <span>
              <IconButton
                aria-label={intl.formatMessage({ id: 'caChat.panel.aria.conversationOptions' })}
                aria-haspopup="true"
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                disabled={!isOwner}
                size="small"
                sx={{ p: 0.5 }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem
              onClick={() => {
                setShareId(activeId)
                setMenuAnchor(null)
              }}
            >
              <ListItemIcon>
                <PersonAddIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{intl.formatMessage({ id: 'caChat.panel.menu.share' })}</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                setRenameId(activeId)
                setMenuAnchor(null)
              }}
            >
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{intl.formatMessage({ id: 'caChat.panel.menu.rename' })}</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                setDeleteId(activeId)
                setMenuAnchor(null)
              }}
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon sx={{ color: 'error.main' }}>
                <DeleteOutlineIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{intl.formatMessage({ id: 'caChat.panel.menu.delete' })}</ListItemText>
            </MenuItem>
          </Menu>
          <Tooltip title={fullscreen ? intl.formatMessage({ id: 'caChat.panel.tooltip.exitFullscreen' }) : intl.formatMessage({ id: 'caChat.panel.tooltip.fullscreen' })}>
            <IconButton
              aria-label={fullscreen ? intl.formatMessage({ id: 'caChat.panel.aria.exitFullscreen' }) : intl.formatMessage({ id: 'caChat.panel.aria.fullscreen' })}
              onClick={() => setFullscreen((v) => !v)}
              size="small"
              sx={{ p: 0.5 }}
            >
              {fullscreen ? (
                <FullscreenExitIcon fontSize="small" />
              ) : (
                <FullscreenIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title={intl.formatMessage({ id: 'caChat.panel.tooltip.close' })}>
            <IconButton
              aria-label={intl.formatMessage({ id: 'caChat.panel.aria.close' })}
              onClick={onClose}
              size="small"
              sx={{ p: 0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Divider />
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {conversations.isError || (!initialContext && agents.isError) ? (
            <Alert
              severity="error"
              sx={{ m: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    conversations.refetch()
                    agents.refetch()
                  }}
                >
                  {intl.formatMessage({ id: 'caChat.panel.button.retry' })}
                </Button>
              }
            >
              {intl.formatMessage({ id: 'caChat.panel.error.bootstrap' })}
            </Alert>
          ) : createError && initialContext ? (
            <Alert
              severity="error"
              sx={{ m: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setCreateError(undefined)
                    create.mutate({ dashboardId: initialContext.dashboardId })
                  }}
                >
                  {intl.formatMessage({ id: 'caChat.panel.button.retry' })}
                </Button>
              }
            >
              {createError}
            </Alert>
          ) : create.isLoading ? (
            <MessageThread turns={[]} loading={true} />
          ) : pickingAgent && !activeId ? (
            <AgentPicker
              backends={supportedBackends}
              agents={agentOptions}
              onPick={pickAgent}
              loading={agents.isLoading}
              error={createError}
            />
          ) : (
            <MessageThread
              key={activeId}
              turns={messages.data ?? []}
              streaming={streaming?.conversationId === activeId ? streaming : null}
              loading={
                conversations.isLoading || (!!activeId && messages.isLoading)
              }
              agent={
                activeAgent
                  ? {
                      name: activeAgent.name,
                      description: activeAgent.description,
                    }
                  : undefined
              }
              onStarter={readOnly || sending ? undefined : handleSend}
            />
          )}
        </Box>
        {readOnly && (
          <Alert severity="info" sx={{ borderRadius: 0 }}>
            {intl.formatMessage({ id: 'caChat.panel.alert.viewOnly' })}
          </Alert>
        )}
        {!readOnly && sendError && (
          <Alert
            severity="error"
            sx={{ borderRadius: 0 }}
            onClose={() => setSendError(undefined)}
          >
            {sendError}
          </Alert>
        )}
        <Divider />
        <MessageInput
          onSend={handleSend}
          pending={sending}
          disabled={readOnly}
          placeholder={
            readOnly ? intl.formatMessage({ id: 'caChat.panel.placeholder.viewOnly' }) : undefined
          }
        />

        {navOpen && (
          <Box
            onClick={() => setNavOpen(false)}
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(0,0,0,0.5)',
              zIndex: 2,
            }}
          />
        )}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            width: 320,
            maxWidth: '85%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
            borderRight: 1,
            borderColor: 'divider',
            boxShadow: navOpen ? 8 : 'none',
            transform: navOpen ? 'translateX(0)' : 'translateX(-100%)',
            transition: 'transform 0.25s ease',
            pointerEvents: navOpen ? 'auto' : 'none',
            zIndex: 3,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              pl: 2,
              pr: 1,
              py: 1,
            }}
          >
            <Typography variant="subtitle1" sx={{ flex: 1 }}>
              {intl.formatMessage({ id: 'caChat.panel.sidebar.title' })}
            </Typography>
            <Tooltip title={intl.formatMessage({ id: 'caChat.panel.tooltip.newConversation' })}>
              <IconButton
                aria-label={intl.formatMessage({ id: 'caChat.panel.aria.newChat' })}
                size="small"
                disabled={create.isLoading}
                onClick={startNew}
              >
                <AddCommentIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={intl.formatMessage({ id: 'caChat.panel.tooltip.close' })}>
              <IconButton
                aria-label={intl.formatMessage({ id: 'caChat.panel.aria.closeConversations' })}
                size="small"
                onClick={() => setNavOpen(false)}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Divider />
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <ConversationSidebar
              items={conversations.data ?? []}
              activeId={activeId}
              onSelect={(id) => {
                setPickingAgent(false)
                setActiveId(id)
                setNavOpen(false)
              }}
              onRename={(id, title) => rename.mutate({ id, title })}
              onShare={(id) => setShareId(id)}
              onDelete={(id) => setDeleteId(id)}
            />
          </Box>
        </Box>
      </Box>
      <ShareConversationDialog
        open={!!shareId}
        conversationId={shareId}
        client={client}
        onClose={() => setShareId(undefined)}
      />
      <RenameConversationDialog
        open={!!renameId}
        conversationId={renameId}
        currentTitle={renameTitle}
        pending={rename.isLoading}
        onClose={() => setRenameId(undefined)}
        onSubmit={(id, title) => rename.mutate({ id, title })}
      />
      <DeleteConversationDialog
        open={!!deleteId}
        conversationId={deleteId}
        conversationTitle={
          conversations.data?.find((c) => c.id === deleteId)?.title
        }
        client={client}
        onClose={() => setDeleteId(undefined)}
        onDeleted={(id) => {
          dropLocal(id)
          setToast(intl.formatMessage({ id: 'caChat.panel.toast.deleted' }))
          if (id === activeId) {
            startNew()
          }
        }}
      />
      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(undefined)}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Drawer>
  )
}
